<template>
  <div class="program-card">
    <!-- ── IMAGE DE FOND (GAUCHE) ─────────────────────────────────────────────── -->
    <div class="program-card__background">
      <PrepeersImage
        v-if="props.program.BackgroundImage"
        :src="props.program.BackgroundImage"
        :alt="`${props.program.Name} background`"
        preset="cardCover"
        image-class="program-card__bg-image"
        sizes="100vw md:400px"
        loading="lazy" />
    </div>

    <!-- ── LOGO (CHEVAUCHANT) ─────────────────────────────────────────────── -->
    <div class="program-card__logo">
      <PrepeersEntityLogo
        :logo-pict="props.program.LogoImage"
        :entity-name="logoEntityName"
        variant="card"
        :alt="`${logoEntityName} logo`" />
    </div>

    <!-- ── CONTENU (DROITE) ─────────────────────────────────────────────── -->
    <div class="program-card__content">
      <!-- Titre programme -->
      <h3 class="program-card__name">{{ props.program.Name }}</h3>

      <!-- Nom école -->
      <div
        v-if="props.program.SchoolName"
        class="program-card__info">
        <span
          class="program-card__icon-slot"
          aria-hidden="true">
          <img
            src="/icons/school-card/icon-sales.svg"
            alt=""
            class="program-card__icon program-card__icon--sales" />
        </span>
        <span class="program-card__type">{{ props.program.SchoolName }}</span>
      </div>

      <!-- Degree level + format (style) -->
      <div
        v-if="props.program.DegreeLevel || props.program.Style"
        class="program-card__info">
        <span
          class="program-card__icon-slot"
          aria-hidden="true">
          <img
            src="/icons/school-card/icon-diploma.svg"
            alt=""
            class="program-card__icon program-card__icon--diploma" />
        </span>
        <span class="program-card__type"
          >{{ props.program.DegreeLevel }} . {{ props.program.Style }}</span
        >
      </div>

      <!-- Localisations -->
      <div
        v-if="props.program.LocationsString"
        class="program-card__info">
        <span
          class="program-card__icon-slot"
          aria-hidden="true">
          <img
            src="/icons/school-card/icon-location-pin.svg"
            alt=""
            class="program-card__icon program-card__icon--pin" />
        </span>
        <span class="program-card__locations">{{ props.program.LocationsString }}</span>
      </div>

      <!-- Bouton -->
      <NuxtLink
        :to="props.program.Link"
        class="program-card__btn"
        @click="onConsultClick">
        Consulter
      </NuxtLink>
    </div>

    <!-- ── ACCENT BORDER (orange right) ─────────────────────────────────────────────── -->
    <div class="program-card__accent" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import PrepeersEntityLogo from '~/components/core/PrepeersEntityLogo.vue'
import PrepeersImage from '~/components/core/PrepeersImage.vue'
import type { ProgramCardInfos } from '~/types/school'

interface ProgramCardProps {
  program: ProgramCardInfos
}

const props = withDefaults(defineProps<ProgramCardProps>(), {})

const emit = defineEmits<{
  consult: []
}>()

/**
 * @returns {void}
 */
function onConsultClick(): void {
  emit('consult')
}

const logoEntityName: ComputedRef<string> = computed(
  () => props.program.SchoolName?.trim() || props.program.Name?.trim() || '',
)
</script>

<style scoped>
/* ════════════════════════════════════════════════════════════
   PROGRAM CARD COMPONENT - LAYOUT HORIZONTAL
   ════════════════════════════════════════════════════════════ */

.program-card {
  position: relative;
  display: flex;
  flex-direction: row;
  height: 100%;
  min-height: 200px;
  background: linear-gradient(135deg, var(--bg-surface) 0%, var(--bg-elevated) 100%);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: all var(--duration-base) var(--ease-out);
}

/* prettier-ignore */
.program-card:hover {
  border-color: var(--accent-orange);
  box-shadow: var(--shadow-lg), 0 0 20px rgba(249, 115, 22, 0.25);
  transform: translateY(-4px);
}

/* ── BACKGROUND IMAGE (GAUCHE) ─────────────────────────────────────────────── */
.program-card__background {
  position: relative;
  flex-shrink: 0;
  width: 90px;
  height: auto;
  min-height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(3, 160, 241, 0.1) 0%, rgba(249, 115, 22, 0.05) 100%);
}

.program-card__bg-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.6;
}

/* ── LOGO (CHEVAUCHANT AU CENTRE) ─────────────────────────────────────────────── */
.program-card__logo {
  position: absolute;
  left: 90px;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  /* background: var(--bg-surface);
  border: 3px solid var(--bg-elevated);
  border-radius: var(--radius-md); */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  /* box-shadow: var(--shadow-md); */
}

/* ── CONTENU (DROITE) ─────────────────────────────────────────────── */
.program-card__content {
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: var(--space-6);
  padding-left: var(--space-12);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  position: relative;
  z-index: 1;
  justify-content: flex-start;
}

/* Titre école */
.program-card__name {
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: var(--leading-snug);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Infos (école, diplôme, localisation) */
.program-card__info {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  min-width: 0;
}

.program-card__icon-slot {
  flex: 0 0 17px;
  width: 17px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.program-card__icon {
  display: block;
  flex-shrink: 0;
  object-fit: contain;
}

.program-card__icon--sales {
  width: 16px;
  height: 15px;
}

.program-card__icon--diploma {
  width: 15px;
  height: 15px;
}

.program-card__icon--pin {
  width: 12px;
  height: 15px;
}

.program-card__type,
.program-card__locations,
.program-card__formations {
  line-height: var(--leading-snug);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

/* ── BOUTON ─────────────────────────────────────────────── */
.program-card__btn {
  align-self: flex-start;
  margin-top: auto;
  padding: var(--space-2) var(--space-4);
  background: var(--bg-elevated);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: 600;
  text-decoration: none;
  transition: all var(--duration-fast) var(--ease-out);
  cursor: pointer;
}

.program-card:hover .program-card__btn {
  border-color: var(--accent-orange);
  color: var(--accent-orange);
  background: rgba(249, 115, 22, 0.1);
}

/* ── ACCENT BORDER (orange right) ─────────────────────────────────────────────── */
.program-card__accent {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 4px;
  background: var(--gradient-orange);
  transition: width var(--duration-base) var(--ease-out);
}

.program-card:hover .program-card__accent {
  width: 6px;
}

/* ── RESPONSIVE ─────────────────────────────────────────────── */
@media (max-width: 768px) {
  .program-card {
    flex-direction: column;
    min-height: auto;
  }

  .program-card__background {
    width: 100%;
    max-width: none;
    height: 140px;
    min-height: 140px;
  }

  .program-card__logo {
    left: 50%;
    top: 140px;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
  }

  .program-card__content {
    padding: var(--space-10) var(--space-5) var(--space-5) var(--space-5);
    gap: var(--space-3);
  }

  .program-card__name {
    font-size: var(--text-base);
    margin-top: var(--space-1);
  }

  .program-card__info {
    font-size: var(--text-xs);
  }

  .program-card__btn {
    margin-top: var(--space-4);
  }
}

@media (max-width: 640px) {
  .program-card__background {
    height: 100px;
    min-height: 100px;
  }

  .program-card__logo {
    top: 100px;
    width: 50px;
    height: 50px;
  }

  .program-card__content {
    padding: var(--space-8) var(--space-4) var(--space-4) var(--space-4);
  }

  .program-card__name {
    font-size: var(--text-sm);
  }
}
</style>
